import utils from "../../../utils/utils";
import { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import { getApiTable } from "./qmsApiUtils";
import { resolveStaffDisplay } from "./qmsVaultUtils";

export const SHARED_WITH_ME_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

export const SHARED_WITH_ME_COLUMN_DATE_PRESETS = DEFAULT_PRESETS;

export function createInitialSharedWithMeFilters() {
  return {
    owners: [],
    sentDateRange: { ...SHARED_WITH_ME_EMPTY_DATE_RANGE },
  };
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function matchesDateRange(value, range) {
  if (!range || range.preset === "All Time" || (!range.startDate && !range.endDate)) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw === "—" || raw.toUpperCase() === "N/A") return false;

  const date = utils.parseDateInput(raw);
  if (!date) return false;

  const start = range.startDate ? startOfDay(utils.parseDateInput(range.startDate)) : null;
  const end = range.endDate ? endOfDay(utils.parseDateInput(range.endDate)) : null;

  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

export function buildSharedWithMeOwnerOptions(rows) {
  const values = new Set();
  (rows || []).forEach((row) => {
    const owner = String(row?.CreatedBy || row?.DocumentOwner || "").trim();
    if (owner) values.add(owner);
  });
  return [...values]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
}

export function applySharedWithMeFilters(rows, filters) {
  if (!filters) return rows || [];

  return (rows || []).filter((row) => {
    if (filters.owners?.length) {
      const owner = String(row?.CreatedBy || row?.DocumentOwner || "").trim();
      if (!filters.owners.includes(owner)) return false;
    }
    if (!matchesDateRange(getSharedWithMeSentDate(row), filters.sentDateRange)) {
      return false;
    }
    return true;
  });
}

export const SHARED_WITH_ME_WORKFLOW = {
  REVIEW: "For Review",
  APPROVAL: "For Approval",
};

export const SHARED_WITH_ME_SECTIONS = [
  {
    id: "review",
    workflow: SHARED_WITH_ME_WORKFLOW.REVIEW,
    label: "Reviewer",
    description: "Documents waiting for your review.",
    tone: "review",
    permission: "review",
  },
  {
    id: "approval",
    workflow: SHARED_WITH_ME_WORKFLOW.APPROVAL,
    label: "Approval",
    description: "Documents waiting for your approval decision.",
    tone: "approval",
    permission: "approve",
  },
];

export function getSharedWithMeDocuments(response) {
  const rows = getApiTable(response, "DMS_SharedWithMe_Documents");
  return (Array.isArray(rows) ? rows : []).filter((row) => !isSharedWithMeFolderRow(row));
}

/** Legacy SQL returns synthetic folder rows (ISFolder=1) for tree navigation — not real documents. */
export function isSharedWithMeFolderRow(row) {
  if (Number(row?.ISFolder ?? row?.isFolder) === 1) return true;

  const title = String(row?.DocumentTitle || "")
    .trim()
    .toLowerCase();
  const url = String(row?.DocumentURL || "").trim();
  const type = String(row?.DocumentType || "").trim();

  if (!url && !type && (title === "for review" || title === "for approval")) {
    return true;
  }

  return false;
}

export function getSharedWithMeWorkflow(row) {
  return String(row?.DocumentParent || "").trim();
}

export function isSharedWithMeReviewRow(row) {
  return getSharedWithMeWorkflow(row) === SHARED_WITH_ME_WORKFLOW.REVIEW;
}

export function isSharedWithMeApprovalRow(row) {
  return getSharedWithMeWorkflow(row) === SHARED_WITH_ME_WORKFLOW.APPROVAL;
}

export function groupSharedWithMeDocuments(rows) {
  const documents = (Array.isArray(rows) ? rows : []).filter((row) => !isSharedWithMeFolderRow(row));

  return SHARED_WITH_ME_SECTIONS.map((section) => ({
    ...section,
    documents: documents.filter((row) => getSharedWithMeWorkflow(row) === section.workflow),
  }));
}

export function getSharedWithMeQueueLabel(row) {
  if (isSharedWithMeReviewRow(row)) return "Reviewer";
  if (isSharedWithMeApprovalRow(row)) return "Approval";
  return "—";
}

export function getSharedWithMeQueueTone(row) {
  if (isSharedWithMeReviewRow(row)) return "review";
  if (isSharedWithMeApprovalRow(row)) return "approval";
  return "neutral";
}

function readSharedStatusValue(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

/**
 * Reviewer's / approver's own progress — not the document owner's cycle stage.
 * "In review" only until that person has submitted from Shared With Me.
 */
export function getSharedWithMeDisplayStage(row, lastActionRow = null) {
  const documentStage = String(row?.DocumentStage || "").trim();

  if (isSharedWithMeReviewRow(row)) {
    const statusRaw = readSharedStatusValue(
      lastActionRow || row,
      "ReviewerStatus",
      "ReviewStatus",
    );
    const statusValue = Number(
      lastActionRow?.ReviewStatus ?? row?.ReviewerStatusValue ?? row?.ReviewStatusValue ?? statusRaw,
    );
    const status = statusRaw.toLowerCase();
    const reviewedAt = readSharedStatusValue(
      lastActionRow || row,
      "ReviewedDateTime",
      "ReviewedDate",
    );
    const complete =
      Number((lastActionRow || row)?.IsReviewComplete) === 1
      || statusValue === 2
      || status.includes("complete");

    if (complete) return "Review completed";
    if (statusValue === 1 || status.includes("inprogress") || status.includes("in progress") || reviewedAt) {
      return "Reviewed";
    }
    return documentStage && documentStage !== "—" ? documentStage : "In review";
  }

  if (isSharedWithMeApprovalRow(row)) {
    const statusRaw = readSharedStatusValue(
      lastActionRow || row,
      "ApproverStatus",
      "ApproveStatus",
    );
    const statusValue = Number(
      lastActionRow?.ApproveStatus ?? row?.ApproverStatusValue ?? statusRaw,
    );
    const status = statusRaw.toLowerCase();
    const approvedAt = readSharedStatusValue(
      lastActionRow || row,
      "ApprovedDateTime",
      "ApprovedDate",
    );

    if (statusValue === 3 || status.includes("approved")) return "Approved";
    if (statusValue === 2 || status.includes("reject")) return "Rejected";
    if (statusValue === 1 || status.includes("inprogress") || approvedAt) return "Responded";
    return documentStage && documentStage !== "—" ? documentStage : "In approval";
  }

  return documentStage;
}

/** ReportTable field metadata — columns for Shared With Me list */
export const SHARED_WITH_ME_FIELD_OPTIONS = [
  { key: "DocumentId", label: "ID" },
  { key: "DocumentTitle", label: "Document" },
  { key: "SharedQueue", label: "Queue" },
  { key: "DocumentStage", label: "Stage" },
  { key: "CreatedBy", label: "Owner" },
  { key: "SentDate", label: "Sent" },
];

export const DEFAULT_SHARED_WITH_ME_COLUMNS = [
  "DocumentId",
  "DocumentTitle",
  "SharedQueue",
  "DocumentStage",
  "CreatedBy",
  "SentDate",
];

export function getSharedWithMeSentDate(row) {
  const sent = String(row?.SentDate || "").trim();
  if (sent) return sent;
  return String(row?.SystemDateTime || "").trim() || "—";
}

export function getSharedWithMeOwnerName(row, staffById = null) {
  const raw = String(row?.CreatedBy || row?.DocumentOwner || "").trim();
  if (!raw) return "";
  if (staffById) return resolveStaffDisplay(staffById, raw);
  return raw;
}

export function getSharedWithMeFieldLabel(field) {
  return SHARED_WITH_ME_FIELD_OPTIONS.find((option) => option.key === field)?.label || field;
}

export function getSharedWithMeExportCellValue(row, field) {
  if (field === "DocumentTitle") {
    return String(row?.DocumentTitle || "").trim() || "—";
  }
  if (field === "DocumentId") {
    const typeAbbr = row?.DocumentTypeAbberiviation || "";
    const catAbbr =
      row?.DocumentCategoryAbberiviation || row?.DocumentCategoryAbbreviation || "";
    const uniqueNum = row?.DocumentCategoryUniqueNumber ?? "";
    const version = row?.VersionNumber ?? row?.versionNumber ?? "";
    if (!typeAbbr && !catAbbr && uniqueNum === "" && version === "") {
      return row?.DocumentId || "—";
    }
    return `${typeAbbr}_${catAbbr}_${uniqueNum}_V${version}`;
  }
  if (field === "SharedQueue") {
    return getSharedWithMeQueueLabel(row);
  }
  if (field === "DocumentStage") {
    return getSharedWithMeDisplayStage(row) || "—";
  }
  if (field === "SentDate") {
    return getSharedWithMeSentDate(row);
  }
  return String(row?.[field] ?? "").trim() || "—";
}

export function filterSharedWithMeDocuments(rows, search = "") {
  const query = String(search || "").trim().toLowerCase();
  if (!query) return rows;

  return (rows || []).filter((row) => {
    const haystack = [
      row?.DocumentTitle,
      row?.DocumentType,
      row?.DocumentTypeAbberiviation,
      row?.DocumentCategory,
      row?.DocumentCategoryAbberiviation,
      row?.CreatedBy,
      row?.DocumentStage,
      row?.VersionNumber,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function getSharedWithMeLastReview(response) {
  return getApiTable(response, "LastReviewData")[0] || null;
}

export function getSharedWithMeLastApproval(response) {
  return getApiTable(response, "LastApproverData")[0] || null;
}

/** Reviewer marked review complete in Shared With Me (ReviewerStatus = 2). */
export function isSharedWithMeReviewActionComplete(reviewRow) {
  if (!reviewRow) return false;
  if (Number(reviewRow.IsReviewComplete) === 1) return true;

  const status = String(reviewRow.ReviewStatus ?? "").trim().toLowerCase();
  if (status === "2") return true;
  if (status.includes("complete")) return true;

  return false;
}

export function getSharedWithMeReviewCompleteMessage() {
  return "You marked this review as complete. You cannot submit another review until the document owner assigns you again.";
}

/**
 * Approver already Approved (3) or Rejected (2) in Shared With Me.
 * After that they cannot submit again until the owner re-assigns them.
 */
export function isSharedWithMeApprovalActionComplete(approvalRow) {
  if (!approvalRow) return false;

  const statusRaw = String(approvalRow.ApproveStatus ?? "").trim();
  const status = statusRaw.toLowerCase();
  if (status === "2" || status === "3") return true;
  if (Number(statusRaw) === 2 || Number(statusRaw) === 3) return true;
  if (status.includes("approved") || status.includes("rejected")) return true;

  const approvedAt = String(approvalRow.ApprovedDateTime || "").trim();
  if (approvedAt && approvedAt !== "—") return true;

  return false;
}

export function getSharedWithMeApprovalCompleteMessage(approvalRow) {
  const status = String(approvalRow?.ApproveStatus ?? "").trim().toLowerCase();
  const decision =
    status === "2" || status.includes("reject")
      ? "rejected"
      : status === "3" || status.includes("approv")
        ? "approved"
        : "submitted a decision on";

  return `You already ${decision} this document. You cannot approve or reject again until the document owner assigns you again.`;
}
